'use client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { useSpecialtyCalculators } from '@/lib/hooks/useSpecialtyCalculators';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types.d';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  getCalculatorOutputAccordionStyles,
  OutputField,
  Styled2ColGrid,
  StyledColumn,
  StyledTextSlider,
} from './components';

type CarDepreciationFormValues = {
  purchasePrice: number;
  gstAmount: number;
  businessUsePercent: number;
  effectiveLifeYears: number;
};

const CarDepreciation = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const theme = useTheme();
  const { calculateCarDepreciation } = useSpecialtyCalculators();

  const methods = useForm<CarDepreciationFormValues>({
    defaultValues: {
      purchasePrice: 50000,
      gstAmount: 4545,
      businessUsePercent: 100,
      effectiveLifeYears: 8,
    },
  });
  const { control, watch } = methods;
  const values = watch();

  const result = calculateCarDepreciation({
    purchasePrice: values.purchasePrice || 0,
    gstAmount: values.gstAmount || 0,
    businessUsePercent: values.businessUsePercent ?? 100,
    effectiveLifeYears: values.effectiveLifeYears || 0,
  });

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="CarDepreciation"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Enter vehicle details'}>
          <Controller
            name="purchasePrice"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Purchase Price (incl. GST)"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="gstAmount"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="GST Amount"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
                helperText="*Typically purchase price ÷ 11"
              />
            )}
          />
          <Controller
            name="businessUsePercent"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Business Use"
                suffix="%"
                value={field.value}
                maxValue={100}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="effectiveLifeYears"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Effective Life"
                suffix=" years"
                value={field.value}
                minValue={1}
                maxValue={20}
                defaultMax={20}
                helperText="*ATO default for cars: 8 years"
                onChange={field.onChange}
              />
            )}
          />
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Depreciation Summary'}>
          <Styled2ColGrid>
            <OutputField label="Cost Base" value={`$${result.costBase}`} />
            <OutputField label="Business Portion" value={`$${result.businessPortion}`} />
          </Styled2ColGrid>
          <Styled2ColGrid>
            <OutputField label="DV Rate" value={result.dvRate} />
            <OutputField
              label="Avg Annual DV Deduction"
              value={`$${result.avgAnnualDvDeduction}`}
            />
          </Styled2ColGrid>
          <OutputField
            label="Avg Annual Prime Cost Deduction"
            value={`$${result.avgAnnualPcDeduction}`}
          />
          {result.schedule.length > 0 && (
            <StyledColumn>
              <Accordion sx={getCalculatorOutputAccordionStyles(theme)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 30 }} />}>
                  <Typography>Depreciation Schedule</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table stickyHeader size="small" sx={{ width: 'auto' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell>DV Opening ($)</TableCell>
                        <TableCell>DV Depreciation ($)</TableCell>
                        <TableCell>DV Closing ($)</TableCell>
                        <TableCell>PC Depreciation ($)</TableCell>
                        <TableCell>PC Closing ($)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.schedule.map((row) => (
                        <TableRow key={row.year}>
                          <TableCell>{row.year}</TableCell>
                          <TableCell>{row.dvOpeningValue}</TableCell>
                          <TableCell>{row.dvDepreciation}</TableCell>
                          <TableCell>{row.dvClosingValue}</TableCell>
                          <TableCell>{row.pcDepreciation}</TableCell>
                          <TableCell>{row.pcClosingValue}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </StyledColumn>
          )}
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default CarDepreciation;
